import { NavLink, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { IDS } from "@/lib/testIds";

export default function Header() {
  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1], delay: 0.05 }}
      className="relative z-40 cs-header"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 40,
        background: "color-mix(in oklab, var(--surface-primary) 82%, transparent)",
        backdropFilter: "saturate(140%) blur(14px)",
        WebkitBackdropFilter: "saturate(140%) blur(14px)",
        borderBottom: "1px solid var(--separator)",
      }}
    >
      <a href="#main" className="skip">Skip to content</a>
      <div className="container-cs cs-header-inner">
        <Link to="/" data-testid={IDS.nav.logo} className="cs-logo" aria-label="Countersign — home">
          {/* Placeholder icon: swap /public/icon.png with the real 1024x1024 app icon */}
          <img
            src="/icon.png"
            srcSet="/icon.png 1x, /icon.png 2x"
            alt="Countersign app icon"
            width="36"
            height="36"
            style={{ width: 36, height: 36, borderRadius: 8, display: "block" }}
          />
          <span className="font-display cs-wordmark">Countersign</span>
        </Link>
        <nav aria-label="Primary" className="cs-nav">
          <NavItem to="/" testId={IDS.nav.home} label="Home" />
          <NavItem to="/privacy" testId={IDS.nav.privacy} label="Privacy" />
          <NavItem to="/support" testId={IDS.nav.support} label="Support" />
          <NavItem to="/accessibility" testId={IDS.nav.accessibility} label="Accessibility" />
        </nav>
      </div>
      <style>{`
        .cs-header-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 14px 0;
          flex-wrap: wrap;
        }
        .cs-logo {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          text-decoration: none;
          color: var(--ink-primary);
          min-height: 0;
        }
        .cs-wordmark {
          font-family: var(--font-display);
          font-size: 22px;
          letter-spacing: -0.01em;
          font-weight: 600;
        }
        .cs-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-wrap: wrap;
        }
        .cs-nav-item {
          padding: 10px 16px;
          min-height: 44px;
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          font-family: var(--font-body);
          font-size: 16px;
          font-weight: 500;
          color: var(--ink-secondary);
          text-decoration: none;
          border: 1px solid transparent;
          background: transparent;
        }
        .cs-nav-item.is-active {
          color: var(--ink-primary);
          border-color: var(--separator);
          background: var(--surface-secondary);
        }

        /* Very narrow — condensed nav so 4 items + logo fit iPhone widths.
           Home is hidden because the logo is already the home link. */
        @media (max-width: 640px) {
          .cs-header-inner { padding: 12px 0; gap: 8px; }
          .cs-wordmark { font-size: 20px; }
          .cs-nav { gap: 2px; }
          .cs-nav-item { padding: 8px 10px; font-size: 14px; min-height: 40px; }
          .cs-nav [data-testid="nav-home-link"] { display: none; }
        }
        @media (max-width: 380px) {
          .cs-wordmark { display: none; }
        }
      `}</style>
    </motion.header>
  );
}

function NavItem({ to, label, testId }) {
  return (
    <NavLink
      to={to}
      end
      data-testid={testId}
      className={({ isActive }) => `cs-nav-item${isActive ? " is-active" : ""}`}
    >
      {label}
    </NavLink>
  );
}
